import { API } from "./config";

export class ApiError extends Error {
  readonly status: number;
  readonly retryAfter?: number;
  constructor(status: number, message: string, retryAfter?: number) {
    super(message);
    this.status = status; this.retryAfter = retryAfter;
  }
}

const TOKEN_KEY = "apex.session";

// localStorage, so the page holds the token — which is why this app loads NO third-party
// script (docs/WEB_APP.md §3). The cookie-session upgrade replaces these three functions.
export function token(): string | null {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
}
function setToken(t: string | null): void {
  try { t ? localStorage.setItem(TOKEN_KEY, t) : localStorage.removeItem(TOKEN_KEY); } catch { /* private mode */ }
  // Everything that reads gated data re-opens on this: a sign-in must light the coverts up and
  // a sign-out (or an expired session) must take them away, without a reload.
  window.dispatchEvent(new Event(SESSION_EVENT));
}
export const SESSION_EVENT = "apex:session";
export const authHeaders = (): Record<string, string> => {
  const t = token();
  return t ? { Authorization: `Bearer ${t}` } : {};
};

async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = {};
  const t = token();
  if (t) headers["Authorization"] = `Bearer ${t}`;
  if (body !== undefined) headers["Content-Type"] = "application/json";
  const r = await fetch(API + path, { method, headers, body: body === undefined ? undefined : JSON.stringify(body) });
  if (r.ok) return (await r.json()) as T;
  // FastAPI's `detail` is a string for most errors and an object for the rate limits.
  let message = r.statusText, retryAfter: number | undefined;
  try {
    const d = (await r.json()).detail;
    if (typeof d === "string") message = d;
    else if (d && typeof d === "object") { message = d.error ?? message; retryAfter = d.retry_after; }
  } catch { /* not JSON */ }
  if (r.status === 401 && t) setToken(null);          // the session is gone; stop sending it
  throw new ApiError(r.status, message, retryAfter);
}

export interface Pack { name: string; bytes: number; updated_at: number }
export interface Entitlement { pro: boolean; tier: string; source: string | null }
export interface Session { token: string; user_id: string; display_name: string }

/** Which bake the covert containers on the server came from (server/packs.py STAMP). */
export interface Stamp { built?: string; coverts?: number; floor_score?: number }
export const packListing = () => call<{ packs: Pack[]; stamp?: Stamp | null }>("GET", "/packs");
export const entitlement = () => call<Entitlement>("GET", "/entitlement");
export const emailStart = (email: string) =>
  call<{ ok: boolean; resend_after: number }>("POST", "/auth/email/start", { email });

const USER_KEY = "apex.user";
/** The signed-in account's id, kept beside the token. Not secret, and it answers one question
 *  the journal cannot otherwise answer: an id starting with `email:` is a desk-only account
 *  that no phone has ever backed up into — so an empty journal there is a LINKING problem, not
 *  a backup problem, and the page must not tell the hunter to turn on a backup that is already on. */
export function userId(): string | null {
  try { return localStorage.getItem(USER_KEY); } catch { return null; }
}

export async function emailVerify(email: string, code: string): Promise<Session> {
  const s = await call<Session>("POST", "/auth/email/verify", { email, code });
  setToken(s.token);
  try { localStorage.setItem(USER_KEY, s.user_id); } catch { /* private mode */ }
  return s;
}

export async function signOut(): Promise<void> {
  try { await call("POST", "/auth/signout"); }
  finally { setToken(null); try { localStorage.removeItem(USER_KEY); } catch { /* private mode */ } }
}

/** One of the phone's backup blobs, decoded. Null when that kind was never backed up (404) —
 *  which is a normal state, not an error: backup is opt-in and off by default on the phone. */
export async function backup<T>(kind: string): Promise<T | null> {
  try {
    const r = await call<{ payload: string }>("GET", `/backup/${kind}`);
    return JSON.parse(r.payload) as T;
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) return null;
    throw e;
  }
}

// ---- crew (server/crew.py, server/chat.py) — the same endpoints the phone calls ----
export interface Member { user_id: string; display_name: string; is_me: boolean }
export interface Party { id: number; code: string; name: string; is_owner: boolean; member_count: number; members: Member[] }
export interface Share { id: number; user_id: string; display_name: string; kind: "pin" | "covert"; label: string | null;
  lat: number; lon: number; score: number | null; cover: string | null; covert_id: number | null; note: string | null;
  pin_kind: string | null; species: string | null; created_at: string; is_mine: boolean }
export interface CrewLocation { user_id: string; display_name: string; lat: number; lon: number; updated_at: string; is_me: boolean }
export interface Message { id: number; user_id: string; display_name: string; body: string; created_at: string;
  client_id: string; share_id: number | null; ref?: { share_id: number; label: string | null; lat: number; lon: number } | null }
export interface CrewSync { messages: Message[]; shares: Share[]; locations: CrewLocation[]; server_time: string }

export const parties = () => call<{ parties: Party[] }>("GET", "/parties").then((r) => r.parties);
export const crewSync = (id: number, afterId: number) => call<CrewSync>("GET", `/party/${id}/sync?after_id=${afterId}`);
export const joinParty = (code: string, displayName: string) =>
  call<Party>("POST", "/party/join", { code, display_name: displayName });
/** `client_id` makes a send idempotent: a retry after a timeout must not double-post. */
export const sendMessage = (id: number, body: string, clientId: string) =>
  call<unknown>("POST", `/party/${id}/messages`, { messages: [{ body, client_id: clientId, elapsed_seconds: 0 }] });
